"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import {
  Bell,
  CheckCheck,
  GraduationCap,
  Headphones,
  Megaphone,
  Music2,
  Trash2,
  WalletCards,
} from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "@/lib/hooks/useAuth";
import {
  deleteAllNotifications,
  deleteNotification,
  markNotificationAsRead,
  subscribeToNotifications,
} from "@/lib/api/notifications";
import type { Notification } from "@/data/types";

type NotificationSection = "activity" | "news";
type NotificationCategory =
  | "all"
  | "feedback"
  | "lesson"
  | "payment"
  | "support";

const categoryOptions: Array<{
  id: NotificationCategory;
  label: string;
}> = [
  { id: "all", label: "전체" },
  { id: "feedback", label: "피드백" },
  { id: "lesson", label: "레슨" },
  { id: "payment", label: "결제·포인트" },
  { id: "support", label: "고객센터" },
];

const feedbackTypes = new Set([
  "feedback_report_ready",
  "new_feedback_request",
  "feedback_request_admin",
  "pro_assigned",
  "work_started",
  "feedback_report",
]);
const lessonTypes = new Set(["video_call", "lesson_consultation"]);
const paymentTypes = new Set([
  "payment_status",
  "pro_settlement",
  "point",
  "points",
  "admin_grant",
]);
const newsTypes = new Set(["event", "promotion", "system_announcement"]);

function isRead(notification: Notification) {
  const legacyNotification = notification as Notification & { read?: boolean };
  return notification.isRead ?? legacyNotification.read ?? false;
}

function getCategory(notification: Notification): NotificationCategory {
  const type = String(notification.type);
  const link = notification.link ?? "";

  if (
    type === "customer_support_reply" ||
    link.startsWith("/profile/inquiries") ||
    link.startsWith("/contact")
  ) {
    return "support";
  }
  if (
    feedbackTypes.has(type) ||
    link.includes("feedback") ||
    link.includes("/profile/reports") ||
    link.includes("expert-analysis")
  ) {
    return "feedback";
  }
  if (
    lessonTypes.has(type) ||
    link.includes("/lessons") ||
    link.includes("/realtime-feedback")
  ) {
    return "lesson";
  }
  if (
    paymentTypes.has(type) ||
    link.includes("/payment") ||
    link.includes("/points")
  ) {
    return "payment";
  }
  return "all";
}

function isNews(notification: Notification) {
  const type = String(notification.type);
  const hasActivityDestination = Boolean(notification.link);
  return newsTypes.has(type) && !hasActivityDestination;
}

function getCategoryIcon(category: NotificationCategory) {
  switch (category) {
    case "feedback":
      return <Music2 className="h-4 w-4" />;
    case "lesson":
      return <GraduationCap className="h-4 w-4" />;
    case "payment":
      return <WalletCards className="h-4 w-4" />;
    case "support":
      return <Headphones className="h-4 w-4" />;
    default:
      return <Bell className="h-4 w-4" />;
  }
}

export function NotificationDropdown() {
  const { user } = useAuth();
  const router = useRouter();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [section, setSection] = useState<NotificationSection>("activity");
  const [category, setCategory] = useState<NotificationCategory>("all");

  useEffect(() => {
    if (!user?.uid) {
      setNotifications([]);
      return;
    }

    return subscribeToNotifications(user.uid, setNotifications, error =>
      console.error("알림 구독 오류:", error)
    );
  }, [user?.uid]);

  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  const unreadCount = useMemo(
    () => notifications.filter(notification => !isRead(notification)).length,
    [notifications]
  );
  const visibleNotifications = useMemo(
    () =>
      notifications.filter(notification => {
        if (section === "news") return isNews(notification);
        if (isNews(notification)) return false;
        return category === "all" || getCategory(notification) === category;
      }),
    [category, notifications, section]
  );

  if (!user?.uid) return null;

  const handleNotificationClick = async (notification: Notification) => {
    if (!isRead(notification)) {
      await markNotificationAsRead(notification.id, user.uid);
    }
    setOpen(false);
    if (notification.link) router.push(notification.link);
  };

  const handleDelete = async (id: string) => {
    const result = await deleteNotification(id, user.uid);
    if (!result.success) {
      toast.error(result.error || "알림을 삭제하지 못했습니다.");
    }
  };

  const handleDeleteAll = async () => {
    const result = await deleteAllNotifications(user.uid);
    if (!result.success) {
      toast.error(result.error || "알림을 삭제하지 못했습니다.");
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(value => !value)}
        aria-label={`알림${unreadCount ? ` ${unreadCount}개` : ""}`}
        title="알림"
        className={`relative flex h-9 w-9 items-center justify-center rounded-full transition-colors ${
          open
            ? "bg-white/10 text-white"
            : "text-gray-400 hover:bg-white/10 hover:text-white"
        }`}
      >
        <Bell className="h-[19px] w-[19px]" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold leading-none text-white ring-2 ring-[#0c0c10]">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed inset-x-3 top-14 z-[120] overflow-hidden rounded-2xl border border-gray-200 bg-white text-gray-900 shadow-2xl md:absolute md:left-auto md:right-0 md:top-11 md:w-[min(410px,calc(100vw-24px))]">
          <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2.5 md:px-4 md:py-3">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-bold">알림</h2>
              {unreadCount > 0 && (
                <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-600">
                  새 알림 {unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {notifications.length > 0 && (
                <button
                  type="button"
                  onClick={() => void handleDeleteAll()}
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-[11px] font-medium text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
                >
                  <CheckCheck className="h-3.5 w-3.5" />
                  모두 삭제
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 border-b border-gray-100">
            {(
              [
                ["activity", "내 알림"],
                ["news", "소식"],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => {
                  setSection(id);
                  setCategory("all");
                }}
                className={`relative py-2.5 text-xs font-semibold transition md:py-3 md:text-sm ${
                  section === id
                    ? "text-gray-950"
                    : "text-gray-400 hover:text-gray-700"
                }`}
              >
                {label}
                {id === "news" && (
                  <Megaphone className="ml-1 inline h-3.5 w-3.5" />
                )}
                {section === id && (
                  <span className="absolute inset-x-5 bottom-0 h-0.5 rounded-full bg-violet-600" />
                )}
              </button>
            ))}
          </div>

          {section === "activity" && (
            <div className="flex gap-1.5 overflow-x-auto border-b border-gray-100 px-3 py-2 [scrollbar-width:none] md:gap-2 md:py-3">
              {categoryOptions.map(option => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setCategory(option.id)}
                  className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-semibold transition md:px-3 md:py-1.5 md:text-[11px] ${
                    category === option.id
                      ? "border-gray-900 bg-gray-900 text-white"
                      : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}

          <div className="max-h-[min(52vh,360px)] min-h-[220px] overflow-y-auto md:max-h-[420px] md:min-h-[280px]">
            {visibleNotifications.length === 0 ? (
              <div className="flex min-h-[280px] flex-col items-center justify-center px-4 py-12 text-center">
                {section === "news" ? (
                  <Megaphone className="h-9 w-9 text-gray-200" />
                ) : (
                  <Bell className="h-9 w-9 text-gray-200" />
                )}
                <p className="mt-3 text-sm font-medium text-gray-500">
                  {section === "news"
                    ? "새로운 소식이 없습니다."
                    : category === "all"
                      ? "새 알림이 없습니다."
                      : `${categoryOptions.find(option => option.id === category)?.label} 알림이 없습니다.`}
                </p>
                <p className="mt-1 text-[11px] text-gray-400">
                  새로운 소식이 생기면 바로 알려드릴게요.
                </p>
              </div>
            ) : (
              visibleNotifications.map(notification => {
                const notificationCategory = getCategory(notification);
                const notificationIsRead = isRead(notification);
                return (
                  <div
                    key={notification.id}
                    className={`group flex cursor-pointer items-start gap-2.5 border-b border-gray-100 px-3 py-2.5 transition-colors last:border-0 md:gap-3 md:px-4 md:py-3 ${
                      notificationIsRead
                        ? "bg-white hover:bg-gray-50"
                        : "bg-violet-50/70 hover:bg-violet-50"
                    }`}
                    onClick={() => void handleNotificationClick(notification)}
                  >
                    <span
                      className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full md:h-8 md:w-8 ${
                        notificationIsRead
                          ? "bg-gray-100 text-gray-400"
                          : "bg-violet-100 text-violet-600"
                      }`}
                    >
                      {section === "news" ? (
                        <Megaphone className="h-4 w-4" />
                      ) : (
                        getCategoryIcon(notificationCategory)
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      {notification.title && (
                        <p className="truncate text-xs font-bold text-gray-900">
                          {notification.title}
                        </p>
                      )}
                      <p className="mt-0.5 line-clamp-2 text-[11px] leading-4 text-gray-600 md:line-clamp-3 md:text-xs md:leading-5">
                        {notification.message}
                      </p>
                      {notification.createdAt && (
                        <p className="mt-1 text-[10px] text-gray-400">
                          {new Date(
                            notification.createdAt.seconds * 1000
                          ).toLocaleString("ko-KR", {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      aria-label="알림 삭제"
                      onClick={event => {
                        event.stopPropagation();
                        void handleDelete(notification.id);
                      }}
                      className="mt-0.5 rounded p-1 text-gray-400 transition hover:bg-white hover:text-rose-500 md:text-gray-300 md:opacity-0 md:group-hover:opacity-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
